import type { NodeView, EditorView } from 'prosemirror-view';
import type { Node as ProseNode } from 'prosemirror-model';
import { XAMIX_ELEMENT_ROOT } from './schema';
import { updateInlineImageAttrs, updateInlineSvgAttrs } from './commands';

/** Normal: 2em. H1–H3: match xamix inline inside heading (2em × heading size). */
const XAMIX_FONT_SIZES: Record<number, number> = { 0: 2, 1: 4, 2: 3, 3: 2.2 };

export function createXamixNodeView(textInput: string, root: string, level: number = 0, bold: boolean = false, brush: boolean = false, color?: string | null): NodeView['dom'] {
  const el = document.createElement('xamix-element') as HTMLElement & { textInput?: string; bold?: boolean; brush?: boolean };
  el.setAttribute('text-input', textInput);
  el.setAttribute('root', root);
  el.setAttribute('font-size', String(XAMIX_FONT_SIZES[level] ?? 2));
  el.setAttribute('unit', 'em');
  if (bold) el.setAttribute('bold', '');
  if (brush) el.setAttribute('brush', '');
  if (color) el.style.color = color;
  (el as unknown as { bold?: boolean; brush?: boolean }).bold = bold;
  (el as unknown as { bold?: boolean; brush?: boolean }).brush = brush;
  el.setAttribute('dir', 'rtl');
  el.contentEditable = 'false';
  el.style.display = 'inline-block';
  el.style.verticalAlign = 'middle';
  el.style.direction = 'rtl';
  el.style.unicodeBidi = 'embed';
  return el;
}

export function xamixInlineNodeView(
  node: ProseNode,
  _view: unknown,
  getPos: () => number | undefined,
  root: string = XAMIX_ELEMENT_ROOT
): NodeView {
  const bold = !!(node.attrs.bold as boolean);
  const brush = !!(node.attrs.brush as boolean);
  const color = (node.attrs as { color?: string | null }).color;
  const dom = createXamixNodeView(node.attrs.textInput || '', root, 0, bold, brush, color);

  return {
    dom,
    update(updatedNode: ProseNode) {
      if (updatedNode.type !== node.type) return false;
      const newBold = !!(updatedNode.attrs.bold as boolean);
      const newBrush = !!(updatedNode.attrs.brush as boolean);
      const newColor = (updatedNode.attrs as { color?: string | null }).color;
      if (newBold !== bold || newBrush !== brush || newColor !== color) {
        return false;
      }
      if (updatedNode.attrs.textInput !== node.attrs.textInput) {
        (dom as HTMLElement).setAttribute('text-input', updatedNode.attrs.textInput || '');
        (dom as unknown as { textInput?: string }).textInput = updatedNode.attrs.textInput || '';
      }
      return true;
    },
    ignoreMutation() {
      return true; // Don't re-parse our content
    },
  };
}

export function blockXamixNodeView(
  node: ProseNode,
  _view: unknown,
  _getPos: () => number | undefined,
  root: string = XAMIX_ELEMENT_ROOT
): NodeView {
  const level = (node.attrs.level as number) || 0;
  const wrapper = document.createElement('div');
  wrapper.className = 'block-xamix' + (level >= 1 && level <= 3 ? ` block-xamix-h${level}` : '');
  wrapper.contentEditable = 'false';
  wrapper.setAttribute('dir', 'rtl');
  wrapper.style.direction = 'rtl';
  wrapper.style.textAlign = 'right';

  const bold = !!(node.attrs.bold as boolean);
  const brush = !!(node.attrs.brush as boolean);
  const color = (node.attrs as { color?: string | null }).color;
  const el = createXamixNodeView(node.attrs.textInput || '', root, level, bold, brush, color);
  wrapper.appendChild(el);

  return {
    dom: wrapper,
    update(updatedNode: ProseNode) {
      if (updatedNode.type !== node.type) return false;
      const newBold = !!(updatedNode.attrs.bold as boolean);
      const newBrush = !!(updatedNode.attrs.brush as boolean);
      const newColor = (updatedNode.attrs as { color?: string | null }).color;
      if (newBold !== bold || newBrush !== brush || newColor !== color) {
        return false;
      }
      const textChanged = updatedNode.attrs.textInput !== node.attrs.textInput;
      const levelChanged = (updatedNode.attrs.level as number) !== level;
      if (textChanged) {
        (el as HTMLElement).setAttribute('text-input', updatedNode.attrs.textInput || '');
        (el as unknown as { textInput?: string }).textInput = updatedNode.attrs.textInput || '';
      }
      if (levelChanged) {
        const newLevel = (updatedNode.attrs.level as number) || 0;
        wrapper.className = 'block-xamix' + (newLevel >= 1 && newLevel <= 3 ? ` block-xamix-h${newLevel}` : '');
        (el as HTMLElement).setAttribute('font-size', String(XAMIX_FONT_SIZES[newLevel] ?? 2));
      }
      return true;
    },
    ignoreMutation() {
      return true;
    },
  };
}

export function inlineImageNodeView(
  node: ProseNode,
  view: EditorView,
  getPos: () => number | undefined,
): NodeView {
  const dom = document.createElement('span');
  dom.className = 'inline-image-wrapper';
  dom.contentEditable = 'false';
  dom.style.display = 'inline-block';
  dom.style.verticalAlign = 'middle';
  dom.style.position = 'relative';
  dom.style.cursor = 'pointer';

  const img = document.createElement('img');
  img.src = node.attrs.src || '';
  img.alt = node.attrs.alt || '';
  if (node.attrs.title) img.title = node.attrs.title;
  if (node.attrs.width != null) img.width = node.attrs.width;
  if (node.attrs.height != null) img.height = node.attrs.height;
  img.style.maxWidth = '100%';
  img.style.display = 'block';
  dom.appendChild(img);

  let popover: HTMLElement | null = null;
  function removePopover() {
    if (popover) { popover.remove(); popover = null; }
  }

  function showPopover() {
    removePopover();
    const pos = getPos();
    if (pos == null) return;
    const currentNode = view.state.doc.nodeAt(pos);
    if (!currentNode) return;

    popover = document.createElement('div');
    popover.className = 'inline-image-popover';
    popover.contentEditable = 'false';
    popover.style.cssText = 'position:absolute;bottom:100%;left:50%;transform:translateX(-50%);background:var(--md-sys-color-surface,#fff);border:1px solid var(--md-sys-color-outline,#ccc);border-radius:8px;padding:8px 12px;display:flex;gap:8px;align-items:center;z-index:100;box-shadow:0 2px 8px rgba(0,0,0,.15);white-space:nowrap;margin-bottom:4px;';

    const wLabel = document.createElement('label');
    wLabel.textContent = 'W:';
    wLabel.style.fontSize = '0.85em';
    const wInput = document.createElement('input');
    wInput.type = 'number';
    wInput.min = '1';
    wInput.step = '1';
    wInput.style.cssText = 'width:60px;padding:2px 4px;border:1px solid #ccc;border-radius:4px;cursor:text;';
    wInput.value = currentNode.attrs.width != null ? String(currentNode.attrs.width) : '';
    wInput.placeholder = 'auto';

    const hLabel = document.createElement('label');
    hLabel.textContent = 'H:';
    hLabel.style.fontSize = '0.85em';
    const hInput = document.createElement('input');
    hInput.type = 'number';
    hInput.min = '1';
    hInput.step = '1';
    hInput.style.cssText = 'width:60px;padding:2px 4px;border:1px solid #ccc;border-radius:4px;cursor:text;';
    hInput.value = currentNode.attrs.height != null ? String(currentNode.attrs.height) : '';
    hInput.placeholder = 'auto';

    const altLabel = document.createElement('label');
    altLabel.textContent = 'Alt:';
    altLabel.style.fontSize = '0.85em';
    const altInput = document.createElement('input');
    altInput.type = 'text';
    altInput.style.cssText = 'width:100px;padding:2px 4px;border:1px solid #ccc;border-radius:4px;';
    altInput.value = currentNode.attrs.alt || '';
    altInput.placeholder = 'alt text';

    const applyBtn = document.createElement('button');
    applyBtn.textContent = 'Aplicar';
    applyBtn.style.cssText = 'padding:2px 10px;border:1px solid #888;border-radius:4px;cursor:pointer;background:#f0f0f0;';
    applyBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const p = getPos();
      if (p == null) return;
      const w = wInput.value ? parseInt(wInput.value, 10) : null;
      const h = hInput.value ? parseInt(hInput.value, 10) : null;
      updateInlineImageAttrs(p, { width: w, height: h, alt: altInput.value })(view.state, view.dispatch);
      removePopover();
    });

    [wLabel, wInput, hLabel, hInput, altLabel, altInput, applyBtn].forEach((el) => popover!.appendChild(el));

    popover.addEventListener('mousedown', (e) => e.stopPropagation());
    popover.addEventListener('click', (e) => e.stopPropagation());
    dom.appendChild(popover);
  }

  dom.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (popover) removePopover();
    else showPopover();
  });

  function onDocClick(e: Event) {
    if (popover && !dom.contains(e.target as Node)) removePopover();
  }
  document.addEventListener('click', onDocClick);

  return {
    dom,
    update(updatedNode: ProseNode) {
      if (updatedNode.type.name !== 'inline_image') return false;
      img.src = updatedNode.attrs.src || '';
      img.alt = updatedNode.attrs.alt || '';
      img.title = updatedNode.attrs.title || '';
      if (updatedNode.attrs.width != null) img.width = updatedNode.attrs.width; else img.removeAttribute('width');
      if (updatedNode.attrs.height != null) img.height = updatedNode.attrs.height; else img.removeAttribute('height');
      return true;
    },
    destroy() {
      removePopover();
      document.removeEventListener('click', onDocClick);
    },
    ignoreMutation() {
      return true;
    },
  };
}

export function inlineSvgNodeView(
  node: ProseNode,
  view: EditorView,
  getPos: () => number | undefined,
): NodeView {
  const dom = document.createElement('span');
  dom.className = 'inline-svg-wrapper';
  dom.contentEditable = 'false';
  dom.style.display = 'inline-block';
  dom.style.verticalAlign = 'middle';
  dom.style.minWidth = '20px';
  dom.style.minHeight = '20px';
  dom.style.cursor = 'pointer';
  dom.style.position = 'relative';

  function cleanSvgContent(content: string): string {
    // Remove XML declaration and DOCTYPE
    return content
      .replace(/<\?xml[^>]*\?>/i, '')
      .replace(/<!DOCTYPE[^>]*>/i, '')
      .replace(/^\s+/, '');
  }

  function renderSvg(content: string, w: number | null, h: number | null) {
    dom.innerHTML = '';
    if (!content) {
      dom.textContent = '[SVG]';
      dom.style.color = '#666';
      dom.style.fontSize = '0.8em';
      return;
    }
    
    const cleanedContent = cleanSvgContent(content);
    const temp = document.createElement('div');
    temp.innerHTML = cleanedContent;
    const svg = temp.querySelector('svg');
    
    if (svg) {
      // Set default dimensions if none exist
      const hasWidth = svg.hasAttribute('width');
      const hasHeight = svg.hasAttribute('height');
      const hasViewBox = svg.hasAttribute('viewBox');
      
      if (!hasViewBox && !hasWidth && !hasHeight) {
        svg.setAttribute('viewBox', '0 0 100 100');
      }
      
      if (w != null) svg.setAttribute('width', String(w));
      if (h != null) svg.setAttribute('height', String(h));
      
      // Ensure SVG is visible
      svg.style.display = 'block';
      svg.style.maxWidth = '100%';
      
      dom.appendChild(svg);
    } else {
      dom.textContent = '[SVG]';
      dom.style.color = '#666';
      dom.style.fontSize = '0.8em';
    }
  }

  renderSvg(node.attrs.content, node.attrs.width, node.attrs.height);

  let popover: HTMLElement | null = null;
  function removePopover() {
    if (popover) { popover.remove(); popover = null; }
  }

  function showPopover() {
    removePopover();
    const pos = getPos();
    if (pos == null) return;
    const currentNode = view.state.doc.nodeAt(pos);
    if (!currentNode) return;

    popover = document.createElement('div');
    popover.className = 'inline-svg-popover';
    popover.contentEditable = 'false';
    // Use fixed positioning and append to body to avoid overflow issues
    const domRect = dom.getBoundingClientRect();
    popover.style.cssText = `position:fixed;left:${domRect.left}px;top:${domRect.bottom + 4}px;background:var(--md-sys-color-surface,#fff);border:1px solid var(--md-sys-color-outline,#ccc);border-radius:8px;padding:8px 12px;display:flex;gap:8px;align-items:center;z-index:1000;box-shadow:0 2px 8px rgba(0,0,0,.15);white-space:nowrap;cursor:move;`;

    // Drag handle
    const dragHandle = document.createElement('div');
    dragHandle.innerHTML = '⋮⋮';
    dragHandle.style.cssText = 'display:flex;flex-direction:column;gap:1px;cursor:move;padding:2px 4px;color:#888;font-size:10px;line-height:8px;user-select:none;';
    dragHandle.title = 'Arrastrar';
    
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let popoverRect: DOMRect | null = null;
    
    dragHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging = true;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      popoverRect = popover!.getBoundingClientRect();
      popover!.style.transform = 'none';
      popover!.style.left = popoverRect.left + 'px';
      popover!.style.top = popoverRect.top + 'px';
      popover!.style.bottom = 'auto';
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isDragging || !popover) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      popover.style.left = (popoverRect!.left + dx) + 'px';
      popover.style.top = (popoverRect!.top + dy) + 'px';
    });
    
    document.addEventListener('mouseup', () => {
      isDragging = false;
    });

    const wLabel = document.createElement('label');
    wLabel.textContent = 'W:';
    wLabel.style.fontSize = '0.85em';
    const wInput = document.createElement('input');
    wInput.type = 'number';
    wInput.min = '1';
    wInput.step = '1';
    wInput.style.cssText = 'width:60px;padding:2px 4px;border:1px solid #ccc;border-radius:4px;cursor:text;';
    wInput.value = currentNode.attrs.width != null ? String(currentNode.attrs.width) : '';
    wInput.placeholder = 'auto';

    const hLabel = document.createElement('label');
    hLabel.textContent = 'H:';
    hLabel.style.fontSize = '0.85em';
    const hInput = document.createElement('input');
    hInput.type = 'number';
    hInput.min = '1';
    hInput.step = '1';
    hInput.style.cssText = 'width:60px;padding:2px 4px;border:1px solid #ccc;border-radius:4px;cursor:text;';
    hInput.value = currentNode.attrs.height != null ? String(currentNode.attrs.height) : '';
    hInput.placeholder = 'auto';

    const applyBtn = document.createElement('button');
    applyBtn.textContent = 'Aplicar';
    applyBtn.style.cssText = 'padding:2px 10px;border:1px solid #888;border-radius:4px;cursor:pointer;background:#f0f0f0;';
    applyBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const p = getPos();
      if (p == null) return;
      const w = wInput.value ? parseInt(wInput.value, 10) : null;
      const h = hInput.value ? parseInt(hInput.value, 10) : null;
      updateInlineSvgAttrs(p, { width: w, height: h })(view.state, view.dispatch);
      removePopover();
    });

    [dragHandle, wLabel, wInput, hLabel, hInput, applyBtn].forEach((el) => popover!.appendChild(el));

    popover.addEventListener('mousedown', (e) => e.stopPropagation());
    popover.addEventListener('click', (e) => e.stopPropagation());
    document.body.appendChild(popover);
  }

  dom.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (popover) removePopover();
    else showPopover();
  });

  function onDocClick(e: Event) {
    if (popover && !dom.contains(e.target as Node)) removePopover();
  }
  document.addEventListener('click', onDocClick);

  return {
    dom,
    update(updatedNode: ProseNode) {
      if (updatedNode.type.name !== 'inline_svg') return false;
      renderSvg(updatedNode.attrs.content, updatedNode.attrs.width, updatedNode.attrs.height);
      return true;
    },
    destroy() {
      removePopover();
      document.removeEventListener('click', onDocClick);
    },
    ignoreMutation() {
      return true;
    },
  };
}
